import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plug, Link2, RefreshCcw, ShoppingBag, Plane, Briefcase, ShoppingCart, MessageCircleMore, CalendarClock, Check, ArrowRight, MapPin } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { useReveal, useCountUp } from "../../hooks/useReveal";
import CheckoutDemo from "./CheckoutDemo";

const productIcons = [Plug, Link2, RefreshCcw, ShoppingCart, MessageCircleMore, CalendarClock];
const productVisuals = [
  // mini visual mock for each product card
  "integrations",
  "paylink",
  "subscriptions",
  "paybutton",
  "collections",
  "services",
];

export const SUBSCRIPTION_PLANS = [
  { id: "monthly", price: 49, months: 1 },
  { id: "quarterly", price: 132, months: 3 },
  { id: "yearly", price: 470, months: 12 },
];

const ServicesVisual = ({ onPay }) => {
  const { t } = useLanguage();
  const [plan, setPlan] = useState("monthly");
  const current = SUBSCRIPTION_PLANS.find((p) => p.id === plan);
  return (
    <div className="flex items-center justify-center py-5">
      <div className="bg-white rounded-xl border border-black/[0.08] shadow-sm px-4 py-3 w-full max-w-[230px]">
        <div className="flex items-center gap-1.5">
          {SUBSCRIPTION_PLANS.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`plan-chip-${p.id}`}
              onClick={() => setPlan(p.id)}
              className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-colors duration-200 ${plan === p.id ? "bg-[#0fe814] text-black" : "bg-black/[0.05] text-black/60 hover:bg-black/10"}`}
            >
              {t.checkoutDemo.plans[p.id]}
            </button>
          ))}
        </div>
        <div className="mt-2.5 flex items-center justify-between text-[11px]">
          <span className="font-semibold text-black/55">Plan Pro</span>
          <span className="font-bold text-black">${current.price.toFixed(2)}</span>
        </div>
        <button
          type="button"
          data-testid="subscribe-demo"
          onClick={() => onPay({ scenario: "subscription", plan })}
          className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] rounded-full py-2 transition-colors duration-200"
        >
          <CalendarClock size={12} /> {t.checkoutDemo.subscribe}
        </button>
      </div>
    </div>
  );
};

const ProductVisual = ({ type, onPay }) => {
  if (type === "integrations") {
    return (
      <div className="flex items-center justify-center gap-3 py-6">
        {["API/Rest", "Webhook"].map((p) => (
          <div key={p} className="bg-white rounded-xl border border-black/[0.08] shadow-sm px-4 py-3 text-[12px] font-bold text-black/60">
            {p}
          </div>
        ))}
      </div>
    );
  }
  if (type === "paylink") {
    return (
      <div className="flex items-center justify-center py-5">
        <div className="bg-white rounded-xl border border-black/[0.08] shadow-sm px-4 py-3 flex items-center gap-2.5">
          <div className="grid grid-cols-3 gap-[3px] shrink-0">
            {Array.from({ length: 9 }).map((_, i) => (
              <span key={i} className={`w-[7px] h-[7px] rounded-[2px] ${i % 2 === 0 ? "bg-black" : "bg-[#0fb013]"}`} />
            ))}
          </div>
          <span className="text-[11px] font-bold text-[#0fb013] whitespace-nowrap">rapidpaypro.com/tu-negocio</span>
        </div>
      </div>
    );
  }
  if (type === "collections") {
    return (
      <div className="flex items-center justify-center py-5">
        <div className="bg-white rounded-xl border border-black/[0.08] shadow-sm px-4 py-3 w-full max-w-[230px]">
          <div className="flex items-center gap-2 text-[10px] font-bold text-black/45">
            <span className="w-2 h-2 rounded-full bg-[#25D366]" /> WhatsApp · Email
          </div>
          <div className="mt-2 bg-[#f1f5f0] rounded-lg rounded-tl-sm px-3 py-2 text-[11px] font-semibold text-black/75 leading-snug">
            Hola María, tu factura #1042 vence hoy.
          </div>
          <button
            type="button"
            data-testid="collection-pay-now"
            onClick={onPay}
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#0fb013] hover:text-black hover:gap-2.5 transition-all duration-200"
          >
            <Link2 size={12} /> Pagar ahora
          </button>
        </div>
      </div>
    );
  }
  if (type === "services") {
    return <ServicesVisual onPay={onPay} />;
  }
  if (type === "paybutton") {
    return (
      <div className="flex items-center justify-center py-5">
        <div className="bg-white rounded-xl border border-black/[0.08] shadow-sm px-4 py-3 flex flex-col items-center gap-2.5 w-full max-w-[220px]">
          <div className="w-full flex items-center justify-between text-[11px] font-semibold text-black/45">
            <span>Total</span>
            <span className="font-bold text-black">$89.00</span>
          </div>
          <button
            type="button"
            data-testid="pay-button-demo"
            onClick={onPay}
            className="w-full inline-flex items-center justify-center gap-2 text-[12px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] active:scale-[0.98] rounded-full py-2.5 shadow-[0_4px_14px_rgba(15,232,20,0.35)] transition-all duration-200"
          >
            <ShoppingCart size={14} />
            Pagar con RapidPay
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center py-5">
      <div className="bg-white rounded-xl border border-black/[0.08] shadow-sm px-5 py-3 flex items-center gap-3">
        <RefreshCcw size={18} className="text-[#0fb013]" />
        <div>
          <div className="text-[12px] font-bold text-black">$29.99 / mes</div>
          <div className="text-[10px] font-semibold text-black/40">Cobro automático · Auto-billing</div>
        </div>
      </div>
    </div>
  );
};

export const Products = () => {
  const { t } = useLanguage();
  const [ref, visible] = useReveal();
  const [checkout, setCheckout] = useState(null);

  return (
    <section id="productos" className="py-20 lg:py-28 bg-white">
      <CheckoutDemo open={Boolean(checkout)} onOpenChange={(v) => !v && setCheckout(null)} scenario={checkout?.scenario} plan={checkout?.plan} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <h2 className="font-extrabold text-[28px] sm:text-[36px] lg:text-[42px] tracking-tight text-black">
          {t.products.title}
        </h2>
        <p className="mt-4 text-[16px] text-black/55 font-medium max-w-2xl">{t.products.subtitle}</p>

        <div
          ref={ref}
          className={`mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {t.products.items.map((item, i) => {
            const Icon = productIcons[i];
            return (
              <div
                key={i}
                data-testid={`product-card-${i}`}
                className="group flex flex-col bg-[#fafcf9] border border-black/[0.06] rounded-3xl p-7 hover:shadow-[0_16px_44px_rgba(15,176,19,0.12)] hover:border-[#0fe814]/40 transition-all duration-300 hover:-translate-y-1.5"
                style={{ transitionDelay: visible ? `${i * 80}ms` : "0ms" }}
              >
                <div className="w-12 h-12 rounded-2xl bg-black flex items-center justify-center mb-6 group-hover:bg-[#0fb013] transition-colors duration-300">
                  <Icon size={22} className="text-[#0fe814] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-[19px] text-black">{item.title}</h3>
                <p className="mt-3 text-[14px] leading-relaxed text-black/55 font-medium flex-1">{item.desc}</p>
                <ProductVisual type={productVisuals[i]} onPay={(cfg) => setCheckout(cfg || { scenario: productVisuals[i] === "collections" ? "collection" : "ecommerce" })} />
                <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#0fb013] group-hover:gap-3 transition-all duration-300">
                  {t.products.learnMore} <ArrowRight size={15} />
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const tabIcons = [ShoppingBag, Plane, Briefcase, ShoppingCart];

export const Industries = () => {
  const { t } = useLanguage();
  const [active, setActive] = useState(0);
  const tab = t.industries.tabs[active];

  return (
    <section id="industrias" className="py-20 lg:py-28 bg-black text-white relative overflow-hidden">
      <div className="absolute bottom-[-300px] left-[-200px] w-[600px] h-[600px] rounded-full bg-[#0fe814]/10 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <h2 className="font-extrabold text-[28px] sm:text-[36px] lg:text-[42px] tracking-tight">
          {t.industries.title}
        </h2>
        <p className="mt-4 text-[16px] text-white font-medium max-w-2xl">{t.industries.subtitle}</p>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-3">
          {t.industries.tabs.map((tb, i) => {
            const Icon = tabIcons[i];
            return (
              <button
                key={tb.name}
                data-testid={`industry-tab-${i}`}
                onClick={() => setActive(i)}
                className={`inline-flex items-center gap-2 text-[13px] font-bold px-6 py-3 rounded-full border-2 transition-all duration-250 ${
                  active === i
                    ? "bg-[#0fe814] border-[#0fe814] text-black shadow-[0_6px_20px_rgba(15,232,20,0.35)]"
                    : "bg-transparent border-white/20 text-white hover:border-white/50 hover:text-white"
                }`}
              >
                <Icon size={16} />
                {tb.name}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="mt-10 grid lg:grid-cols-2 gap-10 items-start" key={active}>
          <div className="industry-fade-in">
            <h3 className="font-bold text-[22px] lg:text-[26px] leading-snug text-[#0fe814]">{tab.headline}</h3>
            <p className="mt-3 text-[15px] text-white font-medium">{tab.desc}</p>
            <ul className="mt-7 space-y-4">
              {tab.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#0fe814]/15 flex items-center justify-center">
                    <Check size={13} className="text-[#0fe814]" />
                  </span>
                  <span className="text-[14px] leading-relaxed text-white font-medium">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="industry-fade-in hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square rounded-[36px] bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 flex items-center justify-center">
              {React.createElement(tabIcons[active], { size: 120, className: "text-[#0fe814]/25", strokeWidth: 1 })}
              <div className="absolute bottom-8 left-8 right-8 bg-white/[0.06] backdrop-blur rounded-2xl border border-white/10 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#0fe814] flex items-center justify-center">
                  <Check size={17} className="text-black" />
                </div>
                <div>
                  <div className="text-[13px] font-bold text-white">$1,250.00 USD</div>
                  <div className="text-[11px] font-medium text-white">Pago aprobado · Payment approved</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatItem = ({ value, suffix, label, active, delay }) => {
  const count = useCountUp(value, active);
  const display = value >= 1000 ? count.toLocaleString("en-US") : count;
  return (
    <div
      className={`text-center lg:text-left transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="font-extrabold text-[40px] lg:text-[52px] tracking-tight text-black leading-none">
        {display}
        <span className="text-[#0fb013]">{suffix}</span>
      </div>
      <div className="mt-2 text-[13px] font-semibold text-black/50">{label}</div>
    </div>
  );
};

export const Stats = () => {
  const { t } = useLanguage();
  const [ref, visible] = useReveal(0.3);

  return (
    <section className="py-20 lg:py-28 bg-[#fafcf9]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-extrabold text-[28px] sm:text-[36px] lg:text-[42px] tracking-tight text-black">
              {t.stats.title}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-black/55 font-medium max-w-xl">{t.stats.subtitle}</p>
            <div className="mt-8 inline-flex items-center gap-2 bg-white border border-black/[0.08] rounded-full px-5 py-2.5 shadow-sm">
              <MapPin size={16} className="text-[#0fb013]" />
              <span className="text-[13px] font-bold text-black">Panamá · Centroamérica · Caribe</span>
            </div>
          </div>
          <div ref={ref} className="grid grid-cols-2 gap-x-8 gap-y-10">
            {t.stats.items.map((s, i) => (
              <StatItem key={i} {...s} active={visible} delay={i * 120} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const CtaBanner = () => {
  const { t } = useLanguage();
  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#0fe814] to-[#0fb013] px-8 py-14 lg:px-16 lg:py-16 text-center">
          <div className="absolute top-[-100px] right-[-80px] w-[300px] h-[300px] rounded-full bg-white/15 blur-2xl pointer-events-none" />
          <h2 className="relative font-extrabold text-[28px] sm:text-[36px] lg:text-[44px] tracking-tight text-black">
            {t.cta.title}
          </h2>
          <p className="relative mt-4 text-[15px] lg:text-[16px] font-semibold text-black/70 max-w-2xl mx-auto">
            {t.cta.subtitle}
          </p>
          <div className="relative mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/registro"
              data-testid="cta-banner-primary"
              className="inline-flex items-center gap-2 text-[14px] font-bold text-white bg-black px-8 py-4 rounded-full hover:bg-black/85 transition-colors duration-200"
            >
              {t.cta.primary} <ArrowRight size={16} />
            </Link>
            <a
              href="#contacto"
              onClick={(e) => scrollTo(e, "#contacto")}
              className="inline-flex items-center text-[14px] font-bold text-black px-8 py-4 rounded-full border-2 border-black hover:bg-black hover:text-white transition-colors duration-200"
            >
              {t.cta.secondary}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
